import React, { useState, useMemo, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { Button, Col, Form, Row, Table, Modal } from "react-bootstrap";
import { Autocomplete, TextField, Box } from "@mui/material";
import { Add, ModeEdit, Delete, Save as SaveIcon } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import DOMPurify from "dompurify";
import TextEditor from "components/TextEditor/TextEditor";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useQuotationCalculations } from "Common/Hooks/UseCalculationForQuotationAndSalesOrder";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import { fetchModel } from "slices/Masters/productsandservices/thunk";
import { fetchProductsDropDownBasedOnModel } from "slices/Masters/productsandservices/thunk";
import { fetchGstByDropDown } from "slices/Masters/gst/thunk";
import { MecordColors } from "Constants/Color";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
// import BillingFromAddress from "Common/DynamicComponentsForEnquiryQuotationSalesOrder/BillingFromAddress";
import BillingFromAddress from "Common/DynamicComponentsForEnquiryQuotationSalesOrder/BillingFromAddress";
import { autocompleteDropdownStyles, getDisabledInputStyles, DETAILS_BG, filterPopoverStyles, tableStyles } from "Constants/Style";
import { convertUTCToLocalDate } from "helpers/DateUtils/dateTimezoneUtils";
import NoSelectedProducts from "assets/images/Order/NoSelectedProducts.png";
import { AiOutlineConsoleSql } from "react-icons/ai";

// Helper function to format date for display (YYYY-MM-DD)
const formatDateForDisplay = (dateString: string | null | undefined): string => {
  if (!dateString) return "";
  // If it's already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  // If it's a UTC ISO string, convert to local date
  if (dateString.includes("T") || dateString.includes("Z")) {
    return convertUTCToLocalDate(dateString);
  }
  return dateString;
};

interface Props {
  onAdd: (values: any) => void;
  items: any[];
  setItems: React.Dispatch<React.SetStateAction<any[]>>;
  isEdit?: boolean;
  id?: string | number;
  company_gst_type?: string;
  billToAddress?: { state?: string } | any;
  // categories: any[];
  // subcategories: any[];
  // makes: any[];
  // models: any[];
  // fetchSubcategories: (categoryId: string) => void;
  // fetchMakes?: (params: { categoryId: string; subcategoryId: string }) => void;
  // fetchModels?: (params: {
  //   categoryId: string;
  //   subcategoryId: string;
  //   makeId: string;
  // }) => void;
  // products: {};
  billFromAddress: any;
  shippingAddresses?: {
    id?: string;
    shipping_address_id?: string;
    address: string;
    city?: string;
    state?: string;
    pin_code?: string;
    pincode?: string;
    phone_number?: string;
    country?: string;
    company_id?: string;
    is_default?: boolean;
    created_at?: string;
    updated_at?: string;
    phone_country?: string;
  }[];
  setShowAddressModal?: (show: boolean) => void;
  setShowProductModal?: (show: boolean) => void;
  composition_gst?: boolean;
  select_composition_gst: string;
  currency_code?: string;
  currency_symbol?: string;
  exchange_rate?: string;
  onAddressAdded?: (newAddress: any) => void; // Callback to directly set address when added
}

type FormValues = {
  category: string;
  category_name: string;
  sub_category: string;
  sub_category_name: string;
  make: string;
  make_name: string;
  model: string;
  model_name: string;
  quantity: string;
  unit: string;
  unit_price: string;
  total: string;
  productName: string;
  service_type: string;
  service_name: string;
  item: string;
  item_name: string;
  discount: number;
  discount_type: "%" | string;
  discount_value: number;
  rate_in: number;
  net_value: number;
  gst_percent: number;
  gst_value: number;
  net_total: number;
  original_gst_percent: number;
  original_gst_value: number;
  // Product-specific fields
  product_code?: string;
  warranty_in_months?: string;
  warranty_date?: string;
  is_serviceable?: boolean;
  // Service-specific fields
  serial_number?: string;
  service_category?: string;
  service_method?: string;
  execution_mode?: string;
  service_amount?: string;
  service_address?: string;
  reported_complaint?: string;
  // installation: "Applicable" | "Not Applicable";
  // installation_date: string;
  // inspection: "Applicable" | "Not Applicable";
  // inspection_date: string;
  shipping_address_id?: string;
  editable?: boolean;
  [key: string]: any;
};

interface ProductDetail {
  id: string | number;
  item_name: string;
  selling_price: number;
  model_name: string;
}

export interface ServiceDetails {
  id: string;
  item_name: string;
  service_code: string;
  category_id: string;
  category_name: string;
  subcategory_id: string;
  subcategory_name: string;
  gst_percent: string;
  selling_price: string;
}

type FormChangeElement =
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement;

// ===== YUP VALIDATION SCHEMA =====
const getValidationSchema = () => {
  return Yup.object().shape({
    model: Yup.string().required("Model is required"),
    productName: Yup.string().required("Product is required"),
    quantity: Yup.string().required("Quantity is required"),
    unit_price: Yup.number()
      .typeError("Unit price must be a number")
      .required("Unit price is required")
      .min(0, "Unit price cannot be negative"),

    discount: Yup.number().min(0, "Discount cannot be negative").optional(),
    // discount_type: Yup.string().oneOf(["%", items.currency_symbol || "₹"]).optional(),
    shipping_address_id: Yup.mixed()
      .required("Shipping Address is required")
      .test(
        "is-valid-shipping-address",
        "Shipping Address is required",
        (value: any) => {
          if (!value) return false;
          // Accept string ID
          if (typeof value === 'string' && value.trim() !== '') return true;
          return false;
        }
      ),
    gst_percent: Yup.number().min(0).max(100).optional(),
  });
};

const initialFormState: FormValues = {
  availability: "In Stock",
  category: "",
  category_name: "",
  sub_category: "",
  sub_category_name: "",
  make: "",
  make_name: "",
  model: "",
  model_name: "",
  quantity: "",
  unit: "",
  unit_price: "",
  total: "",
  productName: "",
  service_type: "",
  service_name: "",
  item: "",
  item_name: "",
  discount: 0,
  discount_type: "",
  discount_value: 0,
  rate_in: 0,
  net_value: 0,
  gst_percent: 0,
  gst_value: 0,
  net_total: 0,
  original_gst_percent: 0,
  original_gst_value: 0,
  // Product-specific fields
  product_code: "",
  warranty_in_months: "",
  warranty_date: "",
  is_serviceable: false,
  // Service-specific fields
  serial_number: "",
  service_category: "",
  service_method: "",
  execution_mode: "",
  service_amount: "",
  service_address: "",
  reported_complaint: "",
  shipping_address_id: "",
  shipping_address: null as any, // Store full address object
  prod_description: "",
  additional_details: "",
  // installation: "Applicable",
  // installation_date: "",
  // inspection_date: "",
  // inspection: "Applicable",
};

const ProductAndServiceSection = forwardRef<any, Props>(({

  onAdd,
  items,
  setItems,
  // categories,
  // subcategories,
  // makes,
  // propModels,
  // fetchSubcategories,
  // fetchMakes,
  // fetchModels,
  billFromAddress,
  shippingAddresses,
  composition_gst,
  select_composition_gst,
  currency_code,
  currency_symbol,
  exchange_rate,
  company_gst_type,
  billToAddress,
  setShowAddressModal,
  setShowProductModal,
  onAddressAdded
}, ref) => {
  const [productData, setProductData] = useState<ProductDetail | null>(null);
  const [editableRowId, setEditableRowId] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<any>(null);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [productOptions, setProductOptions] = useState<ProductDetail[]>([]);
  const [serviceData, setServiceData] = useState<ServiceDetails | null>(null);
  const [serviceNameOptions, setServiceNameOptions] = useState<any[]>([]);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [shippingAddressOptions, setShippingAddressOptions] = useState<any[]>([]);
  const [gstOptions, setGstOptions] = useState<any[]>([]);
  const [selectedGst, setSelectedGst] = useState<any>(null);
  const newlyAddedAddressIdRef = useRef<string | null>(null);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [descriptionModalContent, setDescriptionModalContent] = useState("");
  const [descriptionModalProductName, setDescriptionModalProductName] = useState("");
  const [showAdditionalDetailsModal, setShowAdditionalDetailsModal] = useState(false);
  const [additionalDetailsIndex, setAdditionalDetailsIndex] = useState<number | null>(null);
  const [additionalDetailsDraft, setAdditionalDetailsDraft] = useState("");

  const dispatch = useDispatch<any>();
  const { models } = useSelector((state: any) => state.ProductsAndServices);
  //  console.log("formik values in line 202", formik.values)
  // console.log("models in line 203", models)
  //  console.log("items in line 222 in dynamic product and service form", items)
  const isCompanyCompositionalGstType = useMemo(() => {
    const raw = (company_gst_type || "").toString().trim().toLowerCase();
    if (!raw) return false;
    // Backend may send variations like "Compositional gst", "Composition GST", etc.
    // Make sure it's specifically compositional/composition GST, not just containing the word
    const isCompositional = raw === "compositional gst" || raw === "composition gst" || raw === "compositional" || raw === "composition";
    return isCompositional;
  }, [company_gst_type]);

  // If company gst_type is compositional, no GST should be applied on any item (products + services)
  const shouldZeroGstForCompany = isCompanyCompositionalGstType;

  const normalizeState = (s: any) => (s || "").toString().trim().toLowerCase();
  const billFromState = useMemo(
    () => normalizeState(billFromAddress?.org_state || billFromAddress?.state),
    [billFromAddress]
  );
  const billToState = useMemo(
    () => normalizeState(billToAddress?.state),
    [billToAddress]
  );
  const isIntraState = !billFromState || (!!billToState && billFromState === billToState);
  // ===== FORMIK INITIALIZATION =====
  const formik = useFormik({
    initialValues: initialFormState,
    validationSchema: getValidationSchema(),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  // Expose method to parent to directly set address when added (like ServiceSection does)
  useImperativeHandle(ref, () => ({
    setShippingAddress: (newAddress: any) => {
      if (!newAddress) return;

      // Create full address object (like ServiceSection does)
      console.log("newAddress => in dynamicproductandserviceform", newAddress)
      const addressId = newAddress.shipping_address_id || newAddress.id;
      console.log("addressId => in dynamicproductandserviceform", addressId)
      const fullAddress = {
        shipping_address_id: addressId,
        id: addressId,
        company_id: newAddress.company_id,
        address: newAddress.address,
        city: newAddress.city,
        state: newAddress.state,
        country: newAddress.country || "India",
        pincode: newAddress.pincode || newAddress.pin_code,
        pin_code: newAddress.pincode || newAddress.pin_code,
        phone_number: newAddress.phone_number,
        phone_country: newAddress.phone_country || "+91",
        is_default: newAddress.is_default || false,
        created_at: newAddress.created_at,
        updated_at: newAddress.updated_at
      };
      console.log("fullAddress => in dynamicproductandserviceform", fullAddress)
      // Add to shippingAddressOptions if not already present (like ServiceSection does)
      const exists = shippingAddressOptions.find(
        (opt) => (opt.shipping_address_id || opt.id) === addressId
      );
      console.log("exists => in dynamicproductandserviceform", exists)
      if (!exists) {
        // Store the address ID in ref to track it
        newlyAddedAddressIdRef.current = addressId;

        // Create new option and add it to the list
        const newOption = {
          id: addressId,
          shipping_address_id: addressId,
          name: `${fullAddress.address}, ${fullAddress.city}, ${fullAddress.state}, ${fullAddress.country} - ${fullAddress.pincode}`,
          address: fullAddress.address,
          city: fullAddress.city,
          state: fullAddress.state,
          country: fullAddress.country,
          pincode: fullAddress.pincode,
          pin_code: fullAddress.pincode,
          phone_number: fullAddress.phone_number,
          phone_country: fullAddress.phone_country,
          company_id: fullAddress.company_id,
          is_default: fullAddress.is_default,
          created_at: fullAddress.created_at,
          updated_at: fullAddress.updated_at,
          fullAddress: fullAddress
        };
        console.log("newOption => in dynamicproductandserviceform", newOption)
        setShippingAddressOptions((prev) => [...prev, newOption]);
      } else {
        // Address already exists in options, set it immediately
        newlyAddedAddressIdRef.current = null; // Clear ref since we're setting it directly
        formik.setFieldValue("shipping_address", fullAddress);
        formik.setFieldValue("shipping_address_id", addressId);
        formik.setFieldTouched("shipping_address_id", false);
      }
    }
  }), [shippingAddressOptions, formik]);

  // Auto-select newly added address when it appears in shippingAddressOptions
  useEffect(() => {
    if (newlyAddedAddressIdRef.current && shippingAddressOptions.length > 0) {
      const addressId = newlyAddedAddressIdRef.current;
      const foundOption = shippingAddressOptions.find(
        (opt) => (opt.shipping_address_id || opt.id) === addressId
      );

      if (foundOption) {
        const fullAddress = foundOption.fullAddress || {
          shipping_address_id: addressId,
          id: addressId,
          address: foundOption.address,
          city: foundOption.city,
          state: foundOption.state,
          country: foundOption.country,
          pincode: foundOption.pincode,
          pin_code: foundOption.pincode,
          phone_number: foundOption.phone_number,
          phone_country: foundOption.phone_country,
          company_id: foundOption.company_id,
          is_default: foundOption.is_default,
          created_at: foundOption.created_at,
          updated_at: foundOption.updated_at
        };

        formik.setFieldValue("shipping_address", fullAddress);
        formik.setFieldValue("shipping_address_id", addressId);
        formik.setFieldTouched("shipping_address_id", false);
        newlyAddedAddressIdRef.current = null; // Clear ref after setting
      }
    }
  }, [shippingAddressOptions, formik]);

  const roundToTwoDecimals = (num: number): number => {
    return Math.round(num * 100) / 100;
  };

  const formatDateYYYYMMDD = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const addMonthsFromToday = (months: number) => {
    const base = new Date();
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
    d.setMonth(d.getMonth() + months);
    return d;
  };

  // ===== HELPER FUNCTIONS =====

  const getFieldError = (fieldName: string): string | undefined => {
    return formik.touched[fieldName as keyof typeof formik.touched]
      ? (formik.errors[fieldName as keyof typeof formik.errors] as string)
      : undefined;
  };

  const setFieldValue = (field: string, value: any) => {
    formik.setFieldValue(field, value);
  };

  // ===== EFFECTS =====
  useEffect(() => {
    dispatch(fetchModel("product"));
    console.log("items => in dynamicproductandserviceform useEffect", items)
  }, [dispatch,]);

  useEffect(() => {
    if (models?.data?.length) {
      const modelNames = models.data.map((m: any) => m.model_name);
    }
  }, [models]);

  // Fetch GST Dropdown Options
  useEffect(() => {
    const fetchGstOptions = async () => {
      try {
        const gstRes = await dispatch(fetchGstByDropDown()).unwrap();
        setGstOptions(gstRes?.data || []);
      } catch (err: any) {
        console.error("Error fetching GST options:", err);
        toast.error("Failed to fetch GST options");
      }
    };
    fetchGstOptions();
  }, [dispatch]);

  useEffect(() => { }, [items]);

  // Load Shipping Address Options based on Company (matching ServiceSection pattern)
  useEffect(() => {
    if (shippingAddresses && Array.isArray(shippingAddresses) && shippingAddresses.length > 0) {
      const formattedOptions = shippingAddresses.map((addr: any) => ({
        id: addr.shipping_address_id || addr.id,
        shipping_address_id: addr.shipping_address_id || addr.id,
        name: `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country || "India"} - ${addr.pincode || addr.pin_code}`,
        address: addr.address,
        city: addr.city,
        state: addr.state,
        country: addr.country || "India",
        pincode: addr.pincode || addr.pin_code,
        pin_code: addr.pincode || addr.pin_code,
        phone_number: addr.phone_number,
        phone_country: addr.phone_country,
        company_id: addr.company_id,
        is_default: addr.is_default,
        created_at: addr.created_at,
        updated_at: addr.updated_at,
        // Store the full address object for reference
        fullAddress: addr
      }));
      setShippingAddressOptions(formattedOptions);
    } else {
      // Fallback or empty if no company selected
      setShippingAddressOptions([]);

      // Clear shipping address when company is removed
      if (formik.values.shipping_address || formik.values.shipping_address_id) {
        setFieldValue("shipping_address", null);
        setFieldValue("shipping_address_id", "");
        formik.setFieldTouched("shipping_address_id", false);
      }
    }
  }, [shippingAddresses]);


  useEffect(() => {
    setItems((prev) =>
      prev.map((it) => {
        const rateIn = it.unit_price
          ? it.exchange_rate
            ? Number(it.unit_price) / Number(it.exchange_rate)
            : Number(it.unit_price)
          : Number(it.unit_price);

        return { ...it, rate_in: rateIn };
      })
    );
  }, [currency_code, exchange_rate, items.length]);


  // Currency with composition gst
  useEffect(() => {
    const newCurrency = currency_code;
    console.log("Currency changed - recalculating items. New currency:", newCurrency, "Exchange rate:", exchange_rate);
    if (!Array.isArray(items) || items.length === 0) return;
    const updated = items.map((item) => {
      // Update currency_code and exchange_rate from props
      const currentCurrencyCode = currency_code || item.currency_code || "INR";
      const currentExchangeRate = exchange_rate || item.exchange_rate || 1;

      // Calculate rate_in based on current exchange_rate
      const rate_in = Number(item.unit_price) && Number(currentExchangeRate) > 0
        ? Number(item.unit_price) / Number(currentExchangeRate)
        : Number(item.unit_price) || 0;

      const qty = Number(item.quantity) || 0;

      // Calculate rate_in_currency based on current exchange_rate
      // When currency changes, always recalculate rate_in_currency (don't preserve old backend values)
      let rateInCurrency = 0;
      if (currentCurrencyCode !== "INR" && currentCurrencyCode !== "IND" && Number(currentExchangeRate) > 0) {
        // For foreign currency, calculate rate_in_currency
        rateInCurrency = Number(item.unit_price || 0) / Number(currentExchangeRate);
      } else {
        // For INR/IND, rate_in_currency equals unit_price
        rateInCurrency = Number(item.unit_price || 0);
      }

      // Calculate gross value based on currency for discount calculation
      // When currency changes, always recalculate gross_value (don't preserve old backend values)
      let grossValueForCalc = 0;
      if (currentCurrencyCode !== "INR" && currentCurrencyCode !== "IND") {
        // For foreign currency, use rate_in_currency
        grossValueForCalc = roundToTwoDecimals(rateInCurrency * qty);
      } else {
        // For INR/IND, use unit_price
        grossValueForCalc = roundToTwoDecimals(Number(item.unit_price || 0) * qty);
      }

      const itemTotal = grossValueForCalc; // Use gross value for calculations

      // Discount - calculate discount_amount based on discount_type and discount_value
      // When currency changes, always recalculate discount_amount (don't preserve old backend values)
      let discountValue = Number(item.discount_value) || 0;
      let discountAmount = 0; // Calculated for display only, not sent to backend

      // Always recalculate discount_amount based on current discount_type and discount_value
      if (item.discount_type === "%" || item.discount_type === "percentage") {
        // For percentage, validate and calculate discount_amount
        // Validate: cap discount_value at 100%
        if (discountValue > 100) {
          discountValue = 100;
        }
        // Calculate discount_amount from percentage (for display only)
        discountAmount = itemTotal ? roundToTwoDecimals((itemTotal * discountValue) / 100) : 0;
      } else {
        // For fixed amount (currency), discount_value is the input
        // Validate: discount_value cannot exceed gross value (itemTotal)
        if (discountValue > itemTotal) {
          discountValue = itemTotal;
        }
        // For currency, discount_amount equals discount_value (for display)
        discountAmount = roundToTwoDecimals(discountValue);
      }

      // Calculate net_value: use existing net_value if it's valid, otherwise recalculate
      const calculatedNetValue = roundToTwoDecimals(itemTotal - discountAmount);
      const existingNetValue = Number(item.net_value) || 0;

      // Use existing net_value if it's valid and close to calculated value (within 1% tolerance)
      // This preserves net_value from backend when it's correctly calculated
      const netValue = (existingNetValue > 0 && Math.abs(existingNetValue - calculatedNetValue) / Math.max(existingNetValue, 1) < 0.01)
        ? existingNetValue
        : calculatedNetValue;

      // -----------------------------------------
      // 🚀 GST CALCULATION LOGIC (UPDATED)
      // -----------------------------------------
      let gstValue = 0;

      // If company gst_type is compositional, no GST should be applied on products
      if (!shouldZeroGstForCompany && (newCurrency === "INR" || newCurrency === "IND")) {
        let gstPercent = 0;

        if (composition_gst && select_composition_gst) {
          // Use composition GST only if valid
          const comp = Number(select_composition_gst) || 0;
          gstPercent = comp > 0 ? comp : 0;
        } else {
          // Use standard GST
          gstPercent = Number(item.original_gst_percent) || Number(item.gst_percent) || 0;
        }

        // For quotation items, preserve original GST value if it exists
        // For manually added items, recalculate GST only if netValue > 0
        if (item.original_gst_value && item.original_gst_value > 0) {
          // Preserve original GST value from quotation (even if netValue is 0, as it might be from a different calculation)
          gstValue = Number(item.original_gst_value);
        } else if (netValue > 0) {
          // Recalculate GST for manually added items only if netValue > 0
          gstValue = roundToTwoDecimals((netValue * gstPercent) / 100);
        } else {
          // If netValue is 0 or less and no original_gst_value, GST should be 0
          gstValue = 0;
        }

        return {
          ...item,
          currency_code: currentCurrencyCode, // Update currency_code
          exchange_rate: currentExchangeRate, // Update exchange_rate
          rate_in: rate_in,
          rate_in_currency: rateInCurrency.toFixed(2), // Update rate_in_currency
          gst_percent: shouldZeroGstForCompany ? 0 : Number(gstPercent) || 0,
          gst_value: gstValue,
          net_value: netValue,
          discount_value: discountValue, // Input value (sent to backend)
          discount_amount: discountAmount, // Calculated value (display only)
          gross_value: grossValueForCalc.toFixed(2), // Use calculated gross value
          total_amount: (netValue + gstValue).toFixed(2),
          total: grossValueForCalc.toFixed(2), // Use gross value for total
        };
      }
      // -----------------------------------------

      // For non-INR/IND currencies, set GST to 0
      const isForeignCurrency = currentCurrencyCode !== "INR" && currentCurrencyCode !== "IND";

      return {
        ...item,
        currency_code: currentCurrencyCode, // Update currency_code
        exchange_rate: currentExchangeRate, // Update exchange_rate
        rate_in: rate_in,
        rate_in_currency: rateInCurrency.toFixed(2), // Update rate_in_currency
        gst_percent: (shouldZeroGstForCompany || isForeignCurrency) ? 0 : (Number(item.original_gst_percent) || Number(item.gst_percent) || 0),
        gst_value: 0,
        net_value: netValue,
        discount_value: discountValue, // Input value (sent to backend)
        discount_amount: discountAmount, // Calculated value (display only)
        gross_value: grossValueForCalc.toFixed(2), // Use calculated gross value
        total_amount: (netValue + 0).toFixed(2),
        total: grossValueForCalc.toFixed(2), // Use gross value for total
      };
    });

    setItems(updated);
  }, [
    currency_code,
    exchange_rate,
    formik?.values?.rate_in,
    composition_gst,
    select_composition_gst,
    shouldZeroGstForCompany,
  ]);

  useEffect(() => {
    console.log("items => in dynamicproductandserviceform", items)
    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        cgst_percent:
          shouldZeroGstForCompany || (currency_code !== "INR" && currency_code !== "IND")
            ? 0
            : isIntraState
              ? (Number(item.gst_percent) || 0) / 2
              : 0,
        sgst_percent:
          shouldZeroGstForCompany || (currency_code !== "INR" && currency_code !== "IND")
            ? 0
            : isIntraState
              ? (Number(item.gst_percent) || 0) / 2
              : 0,
        igst_percent:
          shouldZeroGstForCompany || (currency_code !== "INR" && currency_code !== "IND")
            ? 0
            : isIntraState
              ? 0
              : Number(item.gst_percent) || 0,
      }))
    );
  }, [billFromState, billToState, isIntraState, shouldZeroGstForCompany, currency_code, exchange_rate, billToAddress]);

  // ===== EVENT HANDLERS =====

  const handleInputChange = (e: React.ChangeEvent<FormChangeElement>) => {
    const { name, value } = e.target;

    // Prevent negative values for quantity, unit_price, and warranty_in_months
    if ((name === "quantity" || name === "unit_price" || name === "warranty_in_months") && value !== "") {
      const numValue = Number(value);
      if (numValue < 0) {
        return; // Don't update if negative
      }
    }

    setFieldValue(name, value);
    formik.setFieldTouched(name, true);

    // Auto-calculate warranty_date based on warranty_in_months (from today)
    if (name === "warranty_in_months") {
      const months = Number(value);
      if (!value || !Number.isFinite(months) || months <= 0) {
        setFieldValue("warranty_date", "");
      } else {
        setFieldValue("warranty_date", formatDateYYYYMMDD(addMonthsFromToday(months)));
      }
      formik.setFieldTouched("warranty_date", false);
    }
  };

  const handleModelSelect = (_: any, selectedModelData: any | null) => {
    setSelectedModel(selectedModelData);

    if (!selectedModelData) {
      handleFormReset();
      return;
    }

    const model_id = selectedModelData.model_id;


    setSelectedProduct(null);
    setProductData(null);
    setSelectedService(null);
    setServiceNameOptions([]);

    setFieldValue("model", model_id);
    setFieldValue("model_name", selectedModelData.model_name);
    setFieldValue("productName", "");
    setFieldValue("unit_price", "");
    setFieldValue("quantity", "");
    formik.setFieldTouched("model", true);
    console.log("model_id in line 426", model_id)
    dispatch(fetchProductsDropDownBasedOnModel(model_id as any))
      .unwrap()
      .then((payload: any) => {
        const list = Array.isArray(payload) ? payload : [];
        setProductOptions(list);

      })
      .catch(() => toast.error("Error fetching products for selected model"));
  };

  const handleProductSelect = (_: any, selectedProductData: any) => {
    setSelectedProduct(selectedProductData);
    setProductData(selectedProductData || null);

    if (selectedProductData) {
      populateProductData(selectedProductData);
    } else {
      clearProductFields();
    }
  };

  const handleServiceSelect = (_: any, service: any) => {
    setSelectedService(service);
    setSelectedProduct(null);

    if (service) {
      populateServiceData(service);
    } else {
      clearServiceFields();
    }
  };

  const handleFormReset = () => {
    formik.resetForm();
    setProductOptions([]);
    setProductData(null);
    setSelectedModel(null);
    setSelectedProduct(null);
    setServiceNameOptions([]);
    setSelectedService(null);
  };

  const clearProductFields = () => {
    setFieldValue("item", "");
    setFieldValue("productName", "");
    setFieldValue("item_name", "");
    setFieldValue("unit_price", "");
    setFieldValue("category", "");
    setFieldValue("category_name", "");
    setFieldValue("sub_category", "");
    setFieldValue("sub_category_name", "");
    setFieldValue("make", "");
    setFieldValue("make_name", "");
    setFieldValue("gst_percent", 0);
    setFieldValue("hsn", "");
    setFieldValue("hsn_code", "");
    setFieldValue("unit", "");
    setFieldValue("unit_name", "");
    setFieldValue("sku", "");
    setFieldValue("product_code", "");
    setFieldValue("remark", "");
    setFieldValue("product_type", []);
    setFieldValue("is_serviceable", false);
  };

  const clearServiceFields = () => {
    setFieldValue("service_name", "");
    setFieldValue("service_type", "");
    setFieldValue("category", "");
    setFieldValue("category_name", "");
    setFieldValue("sub_category", "");
    setFieldValue("sub_category_name", "");
    setFieldValue("gst_percent", 0);
    setFieldValue("sac", "");
    setFieldValue("serial_number", "");
    setFieldValue("service_category", "");
    setFieldValue("service_method", "");
    setFieldValue("execution_mode", "");
    setFieldValue("service_amount", "");
    setFieldValue("service_address", "");
    setFieldValue("reported_complaint", "");
  };

  const populateProductData = (selectedProductData: any) => {
    // Map product_id (new structure) or id (old structure)
    console.log("selectedProductData in line 534", selectedProductData)
    const productId = selectedProductData?.product_id || selectedProductData?.id || selectedProductData?.item_id || "";

    // Map product_name
    const productName = selectedProductData?.product_name || selectedProductData?.item_name || "";

    // Map unit_price (string in new structure)
    const unitPrice = selectedProductData?.unit_price?.toString() || selectedProductData?.selling_price?.toString() || "";

    // Map category (nested object in new structure)
    const categoryId = selectedProductData?.category?.category_id || selectedProductData?.category_id || selectedProductData?.category || "";
    const categoryName = selectedProductData?.category?.category_name || selectedProductData?.category_name || "";

    // Map sub_category (nested object in new structure)
    const subCategoryId = selectedProductData?.sub_category?.sub_category_id || selectedProductData?.sub_category_id || selectedProductData?.subcategory_id || selectedProductData?.sub_category || "";
    const subCategoryName = selectedProductData?.sub_category?.sub_category_name || selectedProductData?.sub_category_name || selectedProductData?.subcategory_name || "";

    // Map make (nested object in new structure)
    const makeId = selectedProductData?.make?.make_id || selectedProductData?.make_id || selectedProductData?.make || "";
    const makeName = selectedProductData?.make?.make_name || selectedProductData?.make_name || "";

    // Map unit (nested object in new structure)
    const unitId = selectedProductData?.unit?.unit_id || selectedProductData?.unit_id || selectedProductData?.unit || "";
    const unitName = selectedProductData?.unit?.unit_name || selectedProductData?.unit_name || "";

    // Map hsn (nested object in new structure)
    const hsnId = selectedProductData?.hsn?.hsn_code_id || selectedProductData?.hsn_id || selectedProductData?.hsn || "";
    const hsnCode = selectedProductData?.hsn?.hsn_code || selectedProductData?.hsn_code || "";

    // Map gst_percentage (nested in hsn.gst in new structure)
    const originalGstPercent = selectedProductData?.hsn?.gst?.gst_percentage ||
      selectedProductData?.gst_percentage ||
      selectedProductData?.gst_percent ||
      0;

    const gstPercent = shouldZeroGstForCompany ? 0 : originalGstPercent;

    // Map product_sku (new structure) or sku (old structure)
    const sku = selectedProductData?.product_sku || selectedProductData?.sku || selectedProductData?.item_sku || "";

    // Map product_code
    const productCode = selectedProductData?.product_code || "";

    // Map remark
    const remark = selectedProductData?.remark || "";

    // Map product_types (array in new structure) or product_type (old structure)
    const productTypes = selectedProductData?.product_types || selectedProductData?.product_type || [];

    // Set all formik values
    setFieldValue("item", productId);
    setFieldValue("productName", productName);
    setFieldValue("item_name", productName);
    setFieldValue("unit_price", unitPrice);
    setFieldValue("quantity", "");
    setFieldValue("category", categoryId);
    setFieldValue("category_name", categoryName);
    setFieldValue("sub_category", subCategoryId);
    setFieldValue("sub_category_name", subCategoryName);
    setFieldValue("make", makeId);
    setFieldValue("make_name", makeName);
    setFieldValue("gst_percent", Number(gstPercent) || 0);
    setFieldValue("original_gst_percent", Number(originalGstPercent) || 0);
    setFieldValue("hsn", hsnId);
    setFieldValue("hsn_code", hsnCode);
    setFieldValue("unit", unitId);
    setFieldValue("unit_name", unitName);
    setFieldValue("sku", sku);
    setFieldValue("product_code", productCode);
    setFieldValue("remark", remark);
    setFieldValue("product_type", productTypes);

    // Map serviceable field from selected product
    const serviceableValue = selectedProductData?.is_serviceable !== undefined
      ? selectedProductData.is_serviceable
      : (selectedProductData?.serviceable !== undefined ? selectedProductData.serviceable : false);
    setFieldValue("is_serviceable", serviceableValue);

    setFieldValue("prod_description", selectedProductData?.prod_description ?? selectedProductData?.description ?? "");
    setFieldValue("additional_details", selectedProductData?.additional_details ?? "");
  };

  const populateServiceData = (service: any) => {
    setFieldValue("item", service?.id || service?.item_id || "");
    setFieldValue("service_name", service?.item_name || "");
    setFieldValue("item_name", service?.item_name || "");
    setFieldValue("unit_price", service?.unit_price?.toString() || "");
    setFieldValue("service_amount", service?.unit_price?.toString() || service?.selling_price?.toString() || "");
    setFieldValue("quantity", "");
    setFieldValue("category", service?.category_id || "");
    setFieldValue(
      "category_name",
      service?.category_name || service?.category || ""
    );
    setFieldValue("service_category", service?.category_name || service?.category || "");
    setFieldValue(
      "sub_category",
      service?.subcategory_id || service?.sub_category_id || ""
    );
    setFieldValue(
      "sub_category_name",
      service?.subcategory_name || service?.sub_category || ""
    );
    setFieldValue("sac", service?.sac_id || service?.sac || "");
    setFieldValue("sac_id", service?.sac_id || "");
    setFieldValue("original_gst_percent", service?.gst_percent || 0);
    setFieldValue("gst_percent", shouldZeroGstForCompany ? 0 : service?.gst_percent || 0);
  };

  const handleSubmit = (values: any = formik.values) => {
    // Validate form
    if (!formik.isValid) {
      toast.error("Please fill all required fields!");
      return;
    }

    const payload: any = {
      ...values,
      id: Date.now(),
    };

    // Add item details from selected product/service
    if (selectedProduct) {
      payload.composition_gst = composition_gst ? composition_gst : "";
      payload.select_composition_gst = select_composition_gst
        ? select_composition_gst
        : "";
      payload.currency_code = currency_code ? currency_code : "";
      payload.exchange_rate = exchange_rate ? exchange_rate : 1;

      payload.availability = values.availability || "In Stock";

      // Map product_id (new structure) or id (old structure)
      payload.item =
        values.item ||
        selectedProduct.product_id ||
        selectedProduct.id ||
        selectedProduct.item_id || "";
      payload.item_id =
        selectedProduct.product_id ||
        selectedProduct.id ||
        selectedProduct.item_id ||
        values.item || "";

      // Map hsn (nested object in new structure)
      payload.hsn =
        values.hsn ||
        selectedProduct.hsn?.hsn_code_id ||
        selectedProduct.hsn_id ||
        selectedProduct.hsn || "";
      payload.hsn_id =
        selectedProduct.hsn?.hsn_code_id ||
        selectedProduct.hsn_id ||
        selectedProduct.hsn ||
        values.hsn || "";
      payload.hsn_code =
        values.hsn_code ||
        selectedProduct.hsn?.hsn_code ||
        selectedProduct.hsn_code || "";

      // Map category (nested object in new structure)
      payload.category =
        values.category ||
        selectedProduct.category?.category_id ||
        selectedProduct.category_id ||
        selectedProduct.category ||
        "";
      payload.category_name =
        values.category_name ||
        selectedProduct.category?.category_name ||
        selectedProduct.category_name || "";

      // Map sub_category (nested object in new structure)
      payload.sub_category =
        values.sub_category ||
        selectedProduct.sub_category?.sub_category_id ||
        selectedProduct.sub_category_id ||
        selectedProduct.subcategory_id ||
        selectedProduct.sub_category ||
        "";
      payload.sub_category_name =
        values.sub_category_name ||
        selectedProduct.sub_category?.sub_category_name ||
        selectedProduct.sub_category_name ||
        selectedProduct.subcategory_name || "";

      // Map make (nested object in new structure)
      payload.make =
        values.make ||
        selectedProduct.make?.make_id ||
        selectedProduct.make_id ||
        selectedProduct.make || "";
      payload.make_name =
        values.make_name ||
        selectedProduct.make?.make_name ||
        selectedProduct.make_name || "";

      // Map model (already selected, but include for completeness)
      payload.model =
        values.model ||
        selectedProduct.model?.model_id ||
        selectedProduct.model_id ||
        selectedProduct.model || "";
      payload.model_name =
        values.model_name ||
        selectedProduct.model?.model_name ||
        selectedProduct.model_name || "";

      // Map gst_percentage (nested in hsn.gst in new structure)
      const originalGstPercent = Number(selectedProduct.hsn?.gst?.gst_percentage) ||
        Number(selectedProduct.gst_percentage) ||
        selectedProduct.gst_percent ||
        0;

      payload.original_gst_percent = originalGstPercent;
      payload.gst_percent = shouldZeroGstForCompany ? 0 : originalGstPercent;

      // Map unit (nested object in new structure)
      payload.unit =
        values.unit ||
        selectedProduct.unit?.unit_id ||
        selectedProduct.unit_id ||
        selectedProduct.unit || "";
      payload.unit_name =
        values.unit_name ||
        selectedProduct.unit?.unit_name ||
        selectedProduct.unit_name || "";

      // Map sku (product_sku in new structure)
      payload.sku =
        values.sku ||
        selectedProduct.product_sku ||
        selectedProduct.sku ||
        selectedProduct.item_sku || "";

      // Map product_code
      payload.product_code =
        values.product_code ||
        selectedProduct.product_code ||
        "";

      // Map remark
      payload.remark =
        values.remark ||
        selectedProduct.remark || "";

      // Map product_types (array in new structure)
      payload.product_type =
        values.product_type ||
        selectedProduct.product_types ||
        selectedProduct.product_type ||
        [];

      // Map warranty fields - only include if they have values
      payload.warranty_in_months = values.warranty_in_months && values.warranty_in_months.trim() !== "" ? values.warranty_in_months : "";
      payload.warranty_date = values.warranty_date && values.warranty_date.trim() !== "" ? values.warranty_date : "";

      // Map serviceable field - get from form values (set when product is selected) or selectedProduct, default to false
      payload.is_serviceable = values.is_serviceable !== undefined
        ? values.is_serviceable
        : (selectedProduct?.is_serviceable !== undefined
          ? selectedProduct.is_serviceable
          : (selectedProduct?.serviceable !== undefined ? selectedProduct.serviceable : false));
      payload.serviceable = payload.is_serviceable; // Also set serviceable for payload builder compatibility
    } else if (selectedService) {
      payload.composition_gst = composition_gst ? composition_gst : "";
      payload.select_composition_gst = select_composition_gst
        ? select_composition_gst
        : "";
      payload.currency_code = currency_code ? currency_code : "";
      payload.exchange_rate = exchange_rate ? exchange_rate : 1;

      payload.item =
        values.item || selectedService.id || selectedService.item_id || "";

      payload.item_id =
        selectedService.id || selectedService.item_id || values.item || "";

      payload.sac =
        values.sac || selectedService.sac_id || selectedService.sac || "";

      payload.sac_id =
        selectedService.sac_id || selectedService.sac || values.sac || "";

      payload.category =
        values.category ||
        selectedService.category_id ||
        selectedService.category ||
        "";

      payload.sub_category =
        values.sub_category ||
        selectedService.subcategory_id ||
        selectedService.sub_category ||
        "";

      const originalGstPercent = values.original_gst_percent || selectedService.gst_percent || 0;
      payload.original_gst_percent = originalGstPercent;
      payload.gst_percent = shouldZeroGstForCompany ? 0 : originalGstPercent;

      payload.service_code =
        values.service_code || selectedService.service_code || "";

      payload.service_type = values.service_type || "";
      payload.service_name = values.service_name || "";

      // Service-specific fields
      payload.serial_number = values.serial_number || "";
      payload.service_category = values.service_category || "";
      payload.service_method = values.service_method || "";
      payload.execution_mode = values.execution_mode || "";
      payload.service_amount = values.service_amount || values.unit_price || "";
      payload.service_address = values.service_address || "";
      payload.reported_complaint = values.reported_complaint || "";
    }

    // Add shipping address for both products and services
    payload.shipping_address_id = values.shipping_address_id || "";

    payload.rate_in =
      Number(payload.unit_price) / Number(payload.exchange_rate) > 0
        ? Number(payload.unit_price) / Number(payload.exchange_rate)
        : "";

    // Calculate gross value based on currency
    // If currency is not INR, use rate_in_currency; otherwise use unit_price
    let grossValue = 0;
    if (currency_code && currency_code !== "INR" && currency_code !== "IND") {
      // For foreign currency, always recalculate rate_in_currency from current unit_price and exchange_rate
      // This ensures it's correct even if exchange_rate has changed
      const currentExchangeRate = Number(payload.exchange_rate) || 1;
      const rateInCurrency = currentExchangeRate > 0
        ? (Number(payload.unit_price) / currentExchangeRate)
        : Number(payload.unit_price);
      grossValue = rateInCurrency * Number(payload.quantity);
      // Update rate_in_currency and rate_in to match the calculation
      payload.rate_in_currency = rateInCurrency.toFixed(2);
      payload.rate_in = rateInCurrency.toFixed(2);
    } else {
      // For INR, use unit_price
      grossValue = Number(payload.unit_price) * Number(payload.quantity);
      // For INR, rate_in_currency equals unit_price
      payload.rate_in_currency = Number(payload.unit_price).toFixed(2);
      payload.rate_in = Number(payload.unit_price).toFixed(2);
    }

    // Add gross_value to payload
    payload.gross_value = grossValue.toFixed(2);

    // Calculate discount - use discount_value from form
    let discountValue = Number(values.discount_value) || 0;
    let discountAmount = 0; // Calculated for display only, not sent to backend

    if (values.discount_type === "%" || values.discount_type === "percentage") {
      // Validate: If discount_type is "%" or "percentage", cap discount_value at 100%
      if (discountValue > 100) {
        discountValue = 100;
        toast.warning("Discount percentage cannot exceed 100%");
        setFieldValue("discount_value", 100);
        return; // Exit early to prevent submission with invalid discount
      }
      // Calculate discount_amount from percentage (for display only)
      discountAmount = grossValue ? roundToTwoDecimals((grossValue * discountValue) / 100) : 0;
      payload.discount_percent = discountValue; // Include discount_percent when discount_type is "%" or "percentage"
      payload.discount_value = discountValue; // Send discount_value to backend
      payload.discount_type = "%"; // Explicitly set discount_type
    } else {
      // For currency discount, validate it doesn't exceed gross value
      if (discountValue > grossValue) {
        discountValue = grossValue;
        toast.warning("Discount amount cannot exceed gross value");
        setFieldValue("discount_value", grossValue);
        return; // Exit early to prevent submission with invalid discount
      }
      // For currency, discount_amount equals discount_value (for display)
      discountAmount = roundToTwoDecimals(discountValue);
      payload.discount_percent = null; // No percentage when discount_type is not "%"
      payload.discount_value = discountValue; // Send discount_value to backend
      payload.discount_type = values.discount_type || "currency"; // Explicitly set discount_type (use "currency" as default if not set)
    }
//upload
    // Calculate net value (gross value - discount_amount)
    const netValue = roundToTwoDecimals(grossValue - discountAmount);
    payload.net_value = netValue;

    // Get GST percent - use composition GST if applicable, otherwise use standard GST
    let gstPercent = 0;
    if (!shouldZeroGstForCompany && (payload.currency_code === "INR" || payload.currency_code === "IND")) {
      if (Number(payload.select_composition_gst) > 0 && composition_gst) {
        gstPercent = Number(payload.select_composition_gst);
      } else {
        gstPercent = Number(values.gst_percent) || 0;
      }
    }
    payload.gst_percent = gstPercent;

    // Calculate CGST, SGST, and IGST percentages based on bill from/to states
    if (shouldZeroGstForCompany || (payload.currency_code !== "INR" && payload.currency_code !== "IND")) {
      payload.cgst_percent = 0;
      payload.sgst_percent = 0;
      payload.igst_percent = 0;
    } else {
      // Split GST based on BILL FROM vs BILL TO (not shipping address)
      if (!billFromState || !billToState) {
        payload.cgst_percent = 0;
        payload.sgst_percent = 0;
        payload.igst_percent = gstPercent;
      } else if (billFromState === billToState) {
        // Intra-state: CGST + SGST
        payload.cgst_percent = roundToTwoDecimals(gstPercent / 2);
        payload.sgst_percent = roundToTwoDecimals(gstPercent / 2);
        payload.igst_percent = 0;
      } else {
        // Inter-state: IGST
        payload.cgst_percent = 0;
        payload.sgst_percent = 0;
        payload.igst_percent = gstPercent;
      }
    }

    // Company gst_type is compositional => no GST on products
    if (shouldZeroGstForCompany) {
      payload.gst_value = 0;
    } else if (payload.currency_code === "INR" || payload.currency_code === "IND") {
      // Only calculate GST if netValue is greater than 0
      if (netValue > 0) {
        // Calculate GST on net_value, not gross value
        payload.gst_value = roundToTwoDecimals((netValue * gstPercent) / 100);
      } else {
        payload.gst_value = 0;
      }
    } else {
      payload.gst_value = 0;
    }
    payload.total_amount =
      roundToTwoDecimals(Number(payload.gst_value) + Number(payload.net_value));

    if (!payload.item_name) {
      payload.item_name =
        values.productName || values.service_name || values.item_name || "";
    }

    onAdd(payload);
    toast.success("Item added successfully!");
    handleFormReset();
  };

  const handleDelete = (index: number) => {
    const updated = [...items];
    updated.splice(index, 1);
    setItems(updated);
  };

  const handleItemUpdateForItem = (updatedItem: FormValues): FormValues => {
    console.log("handleItemUpdateForItem called with item:", updatedItem);
    console.log("original_gst_value:", updatedItem.original_gst_value);

    if (shouldZeroGstForCompany || (updatedItem.currency_code !== "INR" && updatedItem.currency_code !== "IND")) {
      updatedItem.sgst_percent = 0;
      updatedItem.cgst_percent = 0;
      updatedItem.igst_percent = 0;
      updatedItem.gst_percent = 0;
      updatedItem.gst_value = 0;
      return updatedItem;
    }

    const qty = Number(updatedItem.quantity) || 0;
    const unitPrice = Number(updatedItem.unit_price) || 0;
    const itemTotal = qty * unitPrice;

    // Calculate discount_amount based on discount_type and discount_value
    let discountValue = Number(updatedItem.discount_value) || 0;
    let discountAmount = 0; // Calculated for display only, not sent to backend

    if (updatedItem.discount_type === "%" || updatedItem.discount_type === "percentage") {
      // Validate: cap discount_value at 100%
      if (discountValue > 100) {
        discountValue = 100;
      }
      // Calculate discount_amount from percentage (for display only)
      discountAmount = itemTotal ? roundToTwoDecimals((itemTotal * discountValue) / 100) : 0;
    } else {
      // For currency, validate discount_value doesn't exceed gross value
      if (discountValue > itemTotal) {
        discountValue = itemTotal;
      }
      // For currency, discount_amount equals discount_value (for display)
      discountAmount = roundToTwoDecimals(discountValue);
    }

    const netValue = itemTotal - discountAmount;

    const gstPercent = Number(updatedItem.gst_percent) || 0;

    const billFromStateLocal = billFromState || null;
    const billToStateLocal = billToState || null;

    // Split GST based on BILL FROM vs BILL TO (not shipping address)
    if (!billFromStateLocal || !billToStateLocal) {
      updatedItem.cgst_percent = 0;
      updatedItem.sgst_percent = 0;
      updatedItem.igst_percent = gstPercent;
    } else if (billFromStateLocal === billToStateLocal) {
      updatedItem.cgst_percent = gstPercent / 2;
      updatedItem.sgst_percent = gstPercent / 2;
      updatedItem.igst_percent = 0;
    } else {
      updatedItem.cgst_percent = 0;
      updatedItem.sgst_percent = 0;
      updatedItem.igst_percent = gstPercent;
    }

    updatedItem.discount_value = discountValue; // Input value (sent to backend)
    updatedItem.discount_amount = discountAmount; // Calculated value (display only)
    updatedItem.net_value = netValue;

    // For quotation items, preserve the original GST value only if netValue > 0
    // For manually added items, recalculate GST only if netValue > 0
    console.log("GST calculation - original_gst_value:", updatedItem.original_gst_value, "netValue:", netValue, "gstPercent:", gstPercent);
    if (netValue > 0) {
      if (updatedItem.original_gst_value && updatedItem.original_gst_value > 0) {
        updatedItem.gst_value = updatedItem.original_gst_value;
        console.log("Preserving original GST value for quotation item:", updatedItem.original_gst_value);
      } else {
        updatedItem.gst_value = roundToTwoDecimals((netValue * gstPercent) / 100);
        console.log("Recalculating GST value for manual item:", updatedItem.gst_value);
      }
    } else {
      // If netValue is 0 or less, GST should be 0
      updatedItem.gst_value = 0;
      console.log("Net value is 0, setting GST value to 0");
    }

    updatedItem.total_amount = (netValue + updatedItem.gst_value).toFixed(2);

    return updatedItem;
  };


  return (
    <div
      id="product-service-form"
      className="p-3 rounded mb-4"
      style={{
        background: MecordColors.ActiveBackground,
      }}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit();
        }}
      >
        {/* ROW 1: Model, Product/Service Type, Product Name/Service Name, Quantity/Serial No. */}
        <Row className="g-3 mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Model</Form.Label>
              <Autocomplete
                options={models?.data || []}
                getOptionLabel={(option: any) => option?.model_name || ""}
                value={selectedModel}
                onChange={(e, value) => {
                  handleModelSelect(e, value);
                  formik.setFieldTouched("model", false);
                }}
                onBlur={() => formik.setFieldTouched("model", true)}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.model_name || ""
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Model"
                    variant="outlined"
                    size="small"
                    error={formik.touched.model && !!getFieldError("model")}
                    helperText={formik.touched.model && getFieldError("model")}
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
              />
            </Form.Group>
          </Col>

          <>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Product</Form.Label>
                <Autocomplete
                  options={productOptions}
                  getOptionLabel={(opt: any) => opt?.product_name || opt?.item_name || ""}
                  value={selectedProduct}
                  onChange={(e, value) => {
                    handleProductSelect(e, value);
                    formik.setFieldTouched("productName", false);
                  }}
                  onBlur={() => formik.setFieldTouched("productName", true)}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt?.product_name || opt?.item_name || ""
                    )
                  }
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Product"
                      size="small"
                      error={
                        !!getFieldError("productName") &&
                        formik.touched.productName
                      }
                      helperText={getFieldError("productName")}
                      sx={autocompleteDropdownStyles.inputSx}
                    />
                  )}
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>Product Code</Form.Label>
                <Form.Control
                  type="text"
                  name="product_code"
                  value={formik.values.product_code || ""}
                  onChange={handleInputChange}
                  placeholder="Enter Product Code"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  name="quantity"
                  placeholder="Enter Quantity"
                  min="0"
                  value={formik.values.quantity}
                  onChange={(e) => {
                    handleInputChange(e);
                    formik.setFieldTouched("quantity", false);
                  }}
                  onBlur={formik.handleBlur}
                  isInvalid={!!getFieldError("quantity") && formik.touched.quantity}
                />
                {getFieldError("quantity") && (
                  <Form.Text className="text-danger">
                    {getFieldError("quantity")}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </>
        </Row>


        <Row className="g-3 mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Unit Price</Form.Label>
              <Form.Control
                type="number"
                name="unit_price"
                placeholder="Enter Unit Price"
                min="0"
                value={formik.values.unit_price}
                onChange={(e) => {
                  handleInputChange(e);
                  formik.setFieldValue("rate_in", e.target.value);
                  formik.setFieldTouched("unit_price", false);
                }}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!getFieldError("unit_price") && formik.touched.unit_price
                }
              />
              {getFieldError("unit_price") && (
                <Form.Text className="text-danger">
                  {getFieldError("unit_price")}
                </Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Warranty in months</Form.Label>
              <Form.Control
                type="number"
                name="warranty_in_months"
                min="0"
                value={formik.values.warranty_in_months || ""}
                onChange={handleInputChange}
                placeholder="Enter Warranty in months"
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Warranty date</Form.Label>
              <Form.Control
                type="date"
                name="warranty_date"
                value={formik.values.warranty_date || ""}
                onChange={handleInputChange}
                placeholder="Enter Warranty date"
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0" style={getFieldError("shipping_address_id") ? { color: "red" } : {}}>
                  Shipping Address <span className="text-danger">*</span>
                </Form.Label>
                <span
                  style={{
                    color: MecordColors.ThemeBGColor,
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    fontWeight: "500"
                  }}
                  onClick={() => {
                    if (setShowAddressModal) {
                      setShowAddressModal(true);
                    } else {
                      toast.info("Add New Shipping Address functionality to be implemented");
                    }
                  }}
                >
                  <i className="bi bi-plus-circle" style={{ marginRight: "4px" }}></i>
                  Add New
                </span>
              </div>
              <Autocomplete
                options={shippingAddressOptions}
                getOptionLabel={(option: any) => option.name || ""}
                isOptionEqualToValue={(option: any, value: any) =>
                  option.id === value?.id ||
                  option.shipping_address_id === value?.shipping_address_id
                }
                value={
                  (() => {
                    // If shipping_address is an object, find matching option
                    if (formik.values.shipping_address && typeof formik.values.shipping_address === 'object') {
                      const addressId = formik.values.shipping_address.shipping_address_id || formik.values.shipping_address.id;
                      const found = shippingAddressOptions.find(
                        (s) => s.shipping_address_id === addressId || s.id === addressId
                      );
                      if (found) return found;

                      // If not found in options, create a temporary option from the object
                      const addr = formik.values.shipping_address;
                      return {
                        id: addr.shipping_address_id || addr.id,
                        shipping_address_id: addr.shipping_address_id || addr.id,
                        name: `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country || "India"} - ${addr.pincode || addr.pin_code}`,
                        address: addr.address,
                        city: addr.city,
                        state: addr.state,
                        country: addr.country || "India",
                        pincode: addr.pincode || addr.pin_code,
                        pin_code: addr.pincode || addr.pin_code,
                        phone_number: addr.phone_number,
                        phone_country: addr.phone_country,
                        company_id: addr.company_id,
                        is_default: addr.is_default,
                        created_at: addr.created_at,
                        updated_at: addr.updated_at,
                        fullAddress: addr
                      };
                    }

                    // Legacy support: if shipping_address_id is set, try to find by ID
                    if (formik.values.shipping_address_id) {
                      const found = shippingAddressOptions.find(
                        (s) => s.shipping_address_id === formik.values.shipping_address_id || s.id === formik.values.shipping_address_id
                      );
                      if (found) return found;
                    }

                    return null;
                  })()
                }
                onChange={(event, newValue: any) => {
                  if (newValue) {
                    // Get the full address object from shippingAddresses array
                    const addressId = newValue.shipping_address_id || newValue.id;

                    // First try to get from shippingAddresses (most reliable)
                    let fullAddress = shippingAddresses?.find(
                      (addr: any) => (addr.shipping_address_id || addr.id) === addressId
                    );

                    // If not found, try fullAddress from the option
                    if (!fullAddress && newValue.fullAddress) {
                      fullAddress = newValue.fullAddress;
                    }

                    // If still not found, construct from newValue fields
                    if (!fullAddress) {
                      fullAddress = {
                        shipping_address_id: addressId,
                        company_id: newValue.company_id,
                        address: newValue.address || "",
                        city: newValue.city || "",
                        state: newValue.state || "",
                        country: newValue.country || "India",
                        pincode: newValue.pincode || "",
                        pin_code: newValue.pincode || newValue.pin_code || "",
                        phone_number: newValue.phone_number || "",
                        phone_country: newValue.phone_country || "+91",
                        is_default: newValue.is_default || false,
                        created_at: newValue.created_at,
                        updated_at: newValue.updated_at
                      };
                    }

                    // Ensure all required fields are present
                    if (fullAddress && !fullAddress.shipping_address_id && !(fullAddress as any).id) {
                      console.warn("Incomplete shipping address object:", fullAddress);
                    }

                    if (fullAddress) {
                      setFieldValue("shipping_address", fullAddress);
                      setFieldValue("shipping_address_id", addressId);
                    }
                  } else {
                    setFieldValue("shipping_address", null);
                    setFieldValue("shipping_address_id", "");
                  }
                  if (getFieldError("shipping_address_id")) {
                    formik.setFieldTouched("shipping_address_id", false);
                  }
                }}
                onBlur={() => formik.setFieldTouched("shipping_address_id", true)}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Address"
                    variant="outlined"
                    size="small"
                    error={!!getFieldError("shipping_address_id")}
                    helperText={getFieldError("shipping_address_id")}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      ...getDisabledInputStyles(false),
                    }}
                  />
                )}
              />
            </Form.Group>
          </Col>
        </Row>


        {/* ROW 3: Service Amount, Service Address (for services) */}


        <Row className="g-3 mb-3">
          <Col className="d-flex justify-content-end align-items-end">
            <Button
              variant="primary"
              onClick={() => formik.handleSubmit()}
              className="d-flex align-items-center justify-content-center gap-2"
              style={{
                backgroundColor: MecordColors.PrimaryButtonBGColor,
                borderColor: MecordColors.PrimaryButtonBGColor,
                borderRadius: "8px",
                height: "44px",
                width: "100px",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor =
                  MecordColors.PrimaryButtonHoverBGColor;
                e.currentTarget.style.borderColor =
                  MecordColors.PrimaryButtonHoverBGColor;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor =
                  MecordColors.PrimaryButtonBGColor;
                e.currentTarget.style.borderColor =
                  MecordColors.PrimaryButtonBGColor;
              }}
            >
              <Add fontSize="small" />
              Add
            </Button>
          </Col>
        </Row>


      </Form>

      {/* TABLE SECTION */}
      {Array.isArray(items) && items.length > 0 ? (
        <div
          className="table-responsive bg-white rounded"
          style={{ overflowX: "auto", maxWidth: "100%" }}
        >
          <Table
            hover
            style={{
              minWidth: "2400px",
              tableLayout: "fixed",
              width: "2400px",
            }}
          >
            <thead>
              <tr
                style={{ backgroundColor: MecordColors.LightGrayColor }}
              >
                <th style={{ width: "60px", whiteSpace: "nowrap" }}>Sr No.</th>
                {/* ----------------------- PRODUCT COLUMNS ----------------------- */}

                <>
                  <th style={{ width: "300px", whiteSpace: "nowrap" }}>Products</th>
                  <th style={{ width: "150px", whiteSpace: "nowrap" }}>Additional Details</th>
                  <th style={{ width: "100px", whiteSpace: "nowrap" }}>Make</th>
                  <th style={{ width: "100px", whiteSpace: "nowrap" }}>Model</th>
                  <th style={{ width: "140px", whiteSpace: "nowrap" }}>Product Code</th>
                  <th style={{ width: "140px", whiteSpace: "nowrap" }}>Warranty in Month</th>
                  <th style={{ width: "140px", whiteSpace: "nowrap" }}>Warranty Date</th>
                  <th style={{ width: "120px", whiteSpace: "nowrap" }}>Serviceable</th>
                  <th style={{ width: "120px", whiteSpace: "nowrap" }}>Quantity</th>
                  <th style={{ width: "100px", whiteSpace: "nowrap" }}>Unit</th>
                  <th style={{ width: "120px", whiteSpace: "nowrap" }}>Unit Price (INR)</th>
                  <th style={{ width: "120px", whiteSpace: "nowrap" }}>Rate in Currency</th>
                  <th style={{ width: "120px", whiteSpace: "nowrap" }}>Gross Value</th>
                </>


                {/* ----------------------- COMMON COLUMNS ----------------------- */}
                <th style={{ width: "180px", whiteSpace: "nowrap" }}>Discount/Flat</th>
                <th style={{ width: "110px", whiteSpace: "nowrap" }}>Discount Value</th>
                <th style={{ width: "100px", whiteSpace: "nowrap" }}>Net Value</th>
                <th style={{ width: "80px", whiteSpace: "nowrap" }}>GST (%)</th>
                <th style={{ width: "100px", whiteSpace: "nowrap" }}>SGST (%)</th>
                <th style={{ width: "100px", whiteSpace: "nowrap" }}>CGST (%)</th>
                <th style={{ width: "100px", whiteSpace: "nowrap" }}>IGST (%)</th>
                <th style={{ width: "100px", whiteSpace: "nowrap" }}>GST Value</th>
                <th style={{ width: "100px", whiteSpace: "nowrap" }}>Total</th>
                <th style={{ width: "300px", whiteSpace: "nowrap" }}>Shipping Address</th>


                <th
                  style={{
                    position: "sticky",
                    right: 100,
                    backgroundColor: MecordColors.LightGrayColor,
                    borderLeft: `1px solid ${MecordColors.LightGrayColor}`,
                    zIndex: 10,
                    width: "140px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Product Availability
                </th>

                <th
                  style={{
                    position: "sticky",
                    right: 0,
                    backgroundColor: MecordColors.LightGrayColor,
                    borderLeft: `1px solid ${MecordColors.LightGrayColor}`,
                    zIndex: 10,
                    width: "100px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody style={{ backgroundColor: "transparent" }}>
              {items.map((item: any, index: number) => {
                // Helper function to update item state and recalculate values
                const rowId = item.id;

                const handleItemUpdate = (
                  field?: keyof FormValues,
                  value?: string | number
                ) => {
                  if (!field) return;

                  // Prevent negative values for quantity, unit_price, and warranty_in_months
                  if ((field === "quantity" || field === "unit_price" || field === "warranty_in_months") && value !== "" && value !== null && value !== undefined) {
                    const numValue = typeof value === "string" ? Number(value) : value;
                    if (Number.isFinite(numValue) && numValue < 0) {
                      return; // Don't update if negative
                    }
                  }


                  const updatedItems = [...items];
                  const updatedItem = {
                    ...updatedItems[index],
                    [field]: value,
                  };

                  // Handle availability separately - simple field update, no recalculation needed
                  if (field === "availability") {
                    // Just update availability and return early, no recalculation needed
                    updatedItems[index] = updatedItem;
                    setItems(updatedItems);
                    return;
                  }

                  // Ensure availability is always present for other field updates
                  updatedItem.availability = updatedItem.availability || "In Stock";

                  // ---------------------------
                  // 0️⃣ PRE-CALCULATE GROSS VALUE (needed for discount_type conversion)
                  // ---------------------------
                  const qty = Number(updatedItem.quantity) || 0;
                  const price =
                    updatedItem.rate_in && Number(updatedItem.rate_in) > 0
                      ? Number(updatedItem.rate_in)
                      : Number(updatedItem.unit_price) || 0;

                  // Calculate gross_value for discount conversion
                  let tempGrossValue = 0;
                  if (updatedItem.currency_code && updatedItem.currency_code !== "INR" && updatedItem.currency_code !== "IND") {
                    const currentExchangeRate = Number(updatedItem.exchange_rate) || 1;
                    const rateInCurrency = currentExchangeRate > 0
                      ? (Number(updatedItem.unit_price || 0) / currentExchangeRate)
                      : Number(updatedItem.unit_price || 0);
                    tempGrossValue = roundToTwoDecimals(rateInCurrency * qty);
                  } else {
                    tempGrossValue = roundToTwoDecimals(qty * price);
                  }
                  const currentGrossValue = Number(updatedItem.gross_value) || tempGrossValue;

                  // Handle discount_type change - reset discount_value to 0 when type changes
                  if (field === "discount_type") {
                    const oldDiscountType = updatedItems[index].discount_type;
                    const newDiscountType = value as string;
                    // If discount type is actually changing, reset discount_value to 0
                    if (oldDiscountType !== newDiscountType) {
                      updatedItem.discount_value = 0;
                    }
                  }

                  // Handle warranty_in_months separately - auto-calculate warranty_date
                  if (field === "warranty_in_months") {
                    const months = Number(value);
                    if (value && String(value).trim() !== "" && Number.isFinite(months) && months > 0) {
                      updatedItem.warranty_date = formatDateYYYYMMDD(addMonthsFromToday(months));
                    } else {
                      updatedItem.warranty_date = "";
                    }
                    // Don't recalculate other values for warranty fields, just update and return
                    updatedItems[index] = updatedItem;
                    setItems(updatedItems);
                    return;
                  }

                  // ---------------------------
                  // 1️⃣ QUANTITY + PRICE (supports rate_in)
                  // ---------------------------
                  // qty and price already calculated above for discount_type conversion

                  const itemTotal = roundToTwoDecimals(qty * price); // subtotal with proper rounding
                  updatedItem.total = itemTotal.toFixed(2); // store as subtotal
                  // updatedItem.total_amount = itemTotal.toFixed(2);

                  // Calculate gross_value based on currency
                  // If currency is not INR, use rate_in_currency; otherwise use unit_price
                  let grossValue = 0;
                  if (updatedItem.currency_code && updatedItem.currency_code !== "INR" && updatedItem.currency_code !== "IND") {
                    // For foreign currency, always recalculate rate_in_currency from current unit_price and exchange_rate
                    // This ensures it's correct even if exchange_rate has changed
                    const currentExchangeRate = Number(updatedItem.exchange_rate) || 1;
                    const rateInCurrency = currentExchangeRate > 0
                      ? (Number(updatedItem.unit_price || 0) / currentExchangeRate)
                      : Number(updatedItem.unit_price || 0);
                    grossValue = roundToTwoDecimals(rateInCurrency * qty);
                    // Update rate_in_currency to match the calculation
                    updatedItem.rate_in_currency = rateInCurrency.toFixed(2);
                    // Also update rate_in for consistency
                    updatedItem.rate_in = rateInCurrency;
                  } else {
                    // For INR, use unit_price
                    grossValue = roundToTwoDecimals(Number(updatedItem.quantity || 0) * Number(updatedItem.unit_price || 0));
                    // For INR, rate_in_currency equals unit_price
                    updatedItem.rate_in_currency = Number(updatedItem.unit_price || 0).toFixed(2);
                  }
                  updatedItem.gross_value = grossValue.toFixed(2);

                  // ---------------------------
                  // 2️⃣ DISCOUNT CALCULATION
                  // ---------------------------
                  let discountValue = Number(updatedItem.discount_value) || 0;
                  let discountAmount = 0; // Calculated for display only, not sent to backend

                  // Use grossValue for discount calculations (correctly calculated for foreign currencies)
                  const grossValueForDiscount = Number(updatedItem.gross_value) || grossValue || itemTotal;

                  // Calculate discount_amount based on discount_type
                  if (updatedItem.discount_type === "%" || updatedItem.discount_type === "percentage") {
                    // Validate: cap discount_value at 100%
                    if (discountValue > 100) {
                      discountValue = 100;
                      toast.warning("Discount percentage cannot exceed 100%");
                      updatedItem.discount_value = 100;
                    }
                    // Calculate discount_amount from percentage using grossValue (for display only)
                    discountAmount = grossValueForDiscount
                      ? roundToTwoDecimals((grossValueForDiscount * discountValue) / 100)
                      : 0;
                    // Store percentage in discount_percent
                    updatedItem.discount_percent = discountValue;
                  } else {
                    // For fixed amount (currency), discount_value is the input
                    // Validate: discount_value cannot exceed gross value
                    if (discountValue > grossValueForDiscount) {
                      discountValue = grossValueForDiscount;
                      toast.warning("Discount amount cannot exceed gross value");
                      updatedItem.discount_value = grossValueForDiscount;
                    }
                    // For currency, discount_amount equals discount_value (for display)
                    discountAmount = roundToTwoDecimals(discountValue);
                    // Clear discount_percent when using currency
                    updatedItem.discount_percent = null;
                  }

                  // Store discount_value (sent to backend) and discount_amount (display only)
                  updatedItem.discount_value = discountValue;
                  updatedItem.discount_amount = discountAmount;

                  // ---------------------------
                  // 3️⃣ NET VALUE
                  // ---------------------------
                  // Use grossValue for net value calculation (correctly calculated for foreign currencies)
                  const netValue = roundToTwoDecimals(grossValueForDiscount - discountAmount);
                  updatedItem.net_value = netValue;

                  // ---------------------------
                  // 4️⃣ GST CALCULATION (no CGST/SGST/IGST here)
                  // ---------------------------

                  // ---------------------------
                  // 4️⃣ GST CALCULATION (only when GST fields are updated)
                  // ---------------------------

                  // Only update GST when GST-specific fields are being changed
                  const gstSpecificFields = ['gst_percent', 'original_gst_percent'];

                  if (gstSpecificFields.includes(String(field || ''))) {
                    console.log("GST field being updated, recalculating GST");
                    // If updating gst_percent, use the new value; otherwise use original_gst_percent
                    const gstPercent = shouldZeroGstForCompany
                      ? 0
                      : (field === 'gst_percent'
                        ? Number(value) || 0
                        : (Number(updatedItem.original_gst_percent) || Number(updatedItem.gst_percent) || 0));

                    updatedItem.gst_percent = gstPercent;

                    // Calculate SGST, CGST, IGST based on bill from/to states
                    const billFromStateLocal = billFromState || null;
                    const billToStateLocal = billToState || null;

                    if (shouldZeroGstForCompany || (updatedItem.currency_code && updatedItem.currency_code !== "INR" && updatedItem.currency_code !== "IND")) {
                      updatedItem.cgst_percent = 0;
                      updatedItem.sgst_percent = 0;
                      updatedItem.igst_percent = 0;
                    } else if (!billFromStateLocal || !billToStateLocal) {
                      updatedItem.cgst_percent = 0;
                      updatedItem.sgst_percent = 0;
                      updatedItem.igst_percent = gstPercent;
                    } else if (billFromStateLocal === billToStateLocal) {
                      // Intra-state: Split GST equally between CGST and SGST
                      updatedItem.cgst_percent = roundToTwoDecimals(gstPercent / 2);
                      updatedItem.sgst_percent = roundToTwoDecimals(gstPercent / 2);
                      updatedItem.igst_percent = 0;
                    } else {
                      // Inter-state: Full GST as IGST
                      updatedItem.cgst_percent = 0;
                      updatedItem.sgst_percent = 0;
                      updatedItem.igst_percent = gstPercent;
                    }

                    console.log("=== GST CALCULATION DEBUG ===");
                    console.log("shouldZeroGstForCompany:", shouldZeroGstForCompany);
                    console.log("updatedItem.currency_code:", updatedItem.currency_code);
                    console.log("netValue:", netValue);
                    console.log("gstPercent:", gstPercent);

                    if (!shouldZeroGstForCompany && (updatedItem.currency_code === "INR" || updatedItem.currency_code === "IND")) {
                      // Always recalculate GST value when gst_percent is manually changed
                      // Only calculate GST if netValue is greater than 0
                      updatedItem.gst_value = netValue > 0 ? roundToTwoDecimals(
                        (netValue * gstPercent) / 100
                      ) : 0;
                      console.log("✅ Recalculated GST value:", updatedItem.gst_value, `= (${netValue} * ${gstPercent}) / 100`);
                    } else {
                      updatedItem.gst_value = 0;
                      console.log("❌ GST value set to 0 because:");
                      if (shouldZeroGstForCompany) console.log("  - Company has compositional GST");
                      if (updatedItem.currency_code !== "INR" && updatedItem.currency_code !== "IND") console.log("  - Currency is not INR:", updatedItem.currency_code);
                    }
                  } else {
                    // For all other field updates, recalculate GST value based on current net_value and gst_percent
                    const currentGstPercent = updatedItem.gst_percent || 0;
                    if (!shouldZeroGstForCompany && (updatedItem.currency_code === "INR" || updatedItem.currency_code === "IND")) {
                      // Recalculate GST value based on new net_value
                      updatedItem.gst_value = netValue > 0 ? roundToTwoDecimals(
                        (netValue * currentGstPercent) / 100
                      ) : 0;
                      console.log("Recalculated GST value based on net_value change:", updatedItem.gst_value);
                    } else {
                      updatedItem.gst_value = 0;
                    }
                  }

                  // ---------------------------
                  // 5️⃣ FINAL TOTAL
                  // ---------------------------
                  console.log("")
                  console.log("GST Calculation Details:")
                  console.log("  netValue => ", netValue)
                  console.log("  gst_percent => ", updatedItem.gst_percent)
                  console.log("  gst_value => ", updatedItem.gst_value, `(${netValue} * ${updatedItem.gst_percent}% / 100)`)
                  updatedItem.total_amount = roundToTwoDecimals(
                    netValue + Number(updatedItem.gst_value ?? updatedItem.gst_amount ?? 0)
                  ).toFixed(2);
                  console.log("  total_amount => ", updatedItem.total_amount, `(${netValue} + ${updatedItem.gst_value})`)

                  updatedItems[index] = updatedItem;
                  console.log("Updated item => ", updatedItems[index])
                  setItems(updatedItems);

                };

                return (
                  <tr key={rowId}>
                    {/* SR NO */}
                    <td style={{ width: "60px", textAlign: "center" }}>{index + 1}</td>



                    {/* ---------------- PRODUCT COLUMNS ---------------- */}

                    <>
                      {/* Product Name */}
                      <td style={{ width: "300px", textAlign: "center", verticalAlign: "middle" }}>
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <div>
                            {item?.productName || item?.item_name}
                            <br />
                            <small style={{ fontSize: "0.75rem" }}>
                              SKU:{" "}
                              <span className="text-muted">
                                {item?.product_sku || item?.sku || "N/A"}
                              </span>{" "}
                              HSN Code:{" "}
                              <span className="text-muted">
                                {item?.hsn_code || "N/A"}
                              </span>
                            </small>
                          </div>
                          <InfoIcon
                            style={{
                              cursor: "pointer",
                              color: tableStyles?.primaryColor || MecordColors?.ThemeBGColor,
                              fontSize: "18px",
                              flexShrink: 0,
                            }}
                            onClick={() => {
                              const content = (item as any)?.prod_description ?? (item as any)?.description ?? "";
                              setDescriptionModalContent(content);
                              setDescriptionModalProductName(item?.productName || item?.item_name || "");
                              setShowDescriptionModal(true);
                            }}
                          />
                        </div>
                      </td>

                      {/* Additional Details */}
                      <td style={{ width: "60px", textAlign: "center", verticalAlign: "middle" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <EditIcon
                            style={{
                              cursor: "pointer",
                              color: tableStyles?.primaryColor || MecordColors?.ThemeBGColor,
                              fontSize: "20px",
                            }}
                            onClick={() => {
                              setAdditionalDetailsIndex(index);
                              setAdditionalDetailsDraft((item as any)?.additional_details ?? "");
                              setShowAdditionalDetailsModal(true);
                            }}
                          />
                        </div>
                      </td>

                      {/* Make Name */}
                      <td style={{ width: "100px", textAlign: "center" }}>
                        {item?.make_name}
                      </td>

                      {/* Model */}
                      <td style={{ width: "100px", textAlign: "center" }}>
                        {item?.model_name}
                      </td>

                      {/* Product Code */}
                      <td style={{ width: "140px", textAlign: "center" }}>
                        {editableRowId === item.id ? (
                          <Form.Control
                            type="text"
                            value={item.product_code || ""}
                            onChange={(e) =>
                              handleItemUpdate("product_code", e.target.value)
                            }
                            size="sm"
                          />
                        ) : (
                          item.product_code || "-"
                        )}
                      </td>

                      {/* Warranty in Month */}
                      <td style={{ width: "140px", textAlign: "center" }}>
                        {editableRowId === item.id ? (
                          <Form.Control
                            type="number"
                            min="0"
                            step="1"
                            value={item.warranty_in_months || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "" || Number(val) >= 0) {
                                handleItemUpdate("warranty_in_months", val);
                              }
                            }}
                            size="sm"
                          />
                        ) : (
                          item.warranty_in_months || "-"
                        )}
                      </td>

                      {/* Warranty Date */}
                      <td style={{ width: "140px", textAlign: "center" }}>
                        {formatDateForDisplay(item.warranty_date) || ""}
                      </td>

                      {/* Serviceable Toggle */}
                      <td style={{ width: "120px", textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "1.5rem",
                            cursor: editableRowId === item.id ? "pointer" : "not-allowed",
                            display: "inline-block",
                          }}
                          onClick={() => {
                            if (editableRowId === item.id) {
                              const updatedItems = [...items];
                              updatedItems[index] = {
                                ...updatedItems[index],
                                is_serviceable: !item.is_serviceable,
                              };
                              setItems(updatedItems);
                            }
                          }}
                        >
                          <i
                            className={`bi ${item.is_serviceable
                              ? "bi-toggle-on text-success"
                              : "bi-toggle-off text-danger"
                              }`}
                          />
                        </div>
                      </td>
                    </>


                    {/* ----------- COMMON FIELDS (SERVICE + PRODUCT) ----------- */}

                    {/* Quantity */}
                    <td style={{ width: "120px", textAlign: "center" }}>
                      {editableRowId === item.id ? (
                        <Form.Control
                          type="number"
                          min="0"
                          value={item.quantity}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || Number(val) >= 0) {
                              handleItemUpdate("quantity", val);
                            }
                          }}
                          size="sm"
                        />
                      ) : (
                        item.quantity || "-"
                      )}
                    </td>

                    {/* Unit */}
                    <td style={{ width: "100px", textAlign: "center" }}>
                      {item.unit_name || (item.unit && typeof item.unit === 'string' && isNaN(Number(item.unit)) ? item.unit : null) || "Nos"}
                    </td>

                    {/* Unit Price */}
                    <td style={{ width: "120px", textAlign: "center" }}>
                      {editableRowId === item.id ? (
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          size="sm"
                          value={item.unit_price?.toString() || "0"}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || Number(val) >= 0) {
                              handleItemUpdate("unit_price", parseFloat(val) || 0);
                            }
                          }}
                          style={{ width: "100%" }}
                        />
                      ) : (
                        `₹${Number(item.unit_price || 0).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      )}
                    </td>

                    {/* Rate In Currency */}
                    <td style={{ width: "120px", textAlign: "center" }}>
                      {(() => {
                        const currencySymbol = currency_symbol || "₹";
                        const rateInCurrency = item.rate_in_currency ||
                          (item.unit_price && item.exchange_rate
                            ? (Number(item.unit_price) / Number(item.exchange_rate)).toFixed(2)
                            : item.unit_price?.toFixed(2) || "0");
                        return `${currencySymbol}${parseFloat(rateInCurrency).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`;
                      })()}
                    </td>

                    {/* Gross Value */}
                    <td style={{ width: "120px", textAlign: "center" }}>
                      {(() => {
                        const currencySymbol = currency_symbol || "₹";
                        const grossValue = item.gross_value ||
                          (() => {
                            if (currency_code && currency_code !== "INR" && currency_code !== "IND") {
                              // Always recalculate from current exchange_rate to ensure accuracy
                              const currentExchangeRate = Number(item.exchange_rate) || Number(exchange_rate) || 1;
                              const rateInCurrency = currentExchangeRate > 0
                                ? (Number(item.unit_price || 0) / currentExchangeRate)
                                : Number(item.unit_price || 0);
                              return (Number(item.quantity || 0) * rateInCurrency).toFixed(2);
                            } else {
                              return (Number(item.quantity || 0) * Number(item.unit_price || 0)).toFixed(2);
                            }
                          })();
                        return `${currencySymbol}${parseFloat(grossValue).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`;
                      })()}
                    </td>

                    {/* Product Total (Qty * Price) */}
                    {/* <td style={{ width: "200px" }}>
            {item.total
              ? item.total
              : (Number(item.quantity) * Number(item.unit_price)).toFixed(2)}
          </td> */}

                    {/* Discount */}
                    <td style={{ width: "180px", textAlign: "center" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Form.Control
                          type="number"
                          size="sm"
                          value={(item.discount_value ?? 0).toString()}
                          readOnly={editableRowId !== item.id}
                          onChange={(e) => {
                            if (editableRowId === item.id) {
                              const value = parseFloat(e.target.value) || 0;
                              const discountType = item.discount_type || "currency";
                              const grossValue = item.gross_value || 0;

                              // Validation based on discount type
                              if (
                                discountType === "percentage" &&
                                value > 100
                              )
                                return;
                              if (
                                discountType === "currency" &&
                                value > grossValue
                              )
                                return;

                              handleItemUpdate("discount_value", value);
                            }
                          }}
                          min="0"
                          step="0.01"
                          max={
                            item.discount_type === "percentage"
                              ? "100"
                              : item.gross_value?.toString()
                          }
                          style={{ flex: 1 }}
                          disabled={editableRowId !== item.id}
                        />

                        <Form.Select
                          size="sm"
                          value={item.discount_type || "currency"}
                          disabled={editableRowId !== item.id}
                          onChange={(e) => {
                            if (editableRowId === item.id) {
                              const discountType = e.target.value as
                                | "percentage"
                                | "currency";
                              // handleItemUpdate will automatically reset discount_value to 0 when discount_type changes
                              handleItemUpdate(
                                "discount_type",
                                discountType
                              );
                            }
                          }}
                          style={{ width: "50px", padding: "4px" }}
                        >
                          <option value="currency">
                            {currency_symbol || "₹"}
                          </option>
                          <option value="percentage">%</option>
                        </Form.Select>
                      </div>
                    </td>
                    <td style={{ width: "110px", textAlign: "center" }}>
                      {(() => {
                        const currencySymbol = currency_symbol || "₹";
                        const discountValue = (item.discount_type === "percentage" || item.discount_type === "%")
                          ? (item.discount_amount || 0)
                          : (item.discount_value || 0);
                        return `${currencySymbol}${parseFloat(discountValue.toString()).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`;
                      })()}
                    </td>
                    <td style={{ width: "100px", textAlign: "center" }}>
                      {(() => {
                        const currencySymbol = currency_symbol || "₹";
                        return `${currencySymbol}${parseFloat((item.net_value || 0).toString()).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`;
                      })()}
                    </td>
                    <td style={{ width: "80px", textAlign: "center" }}>
                      {editableRowId === item.id &&
                        !shouldZeroGstForCompany &&
                        !(item.currency_code && item.currency_code !== "INR" && item.currency_code !== "IND") &&
                        !(currency_code && currency_code !== "INR" && currency_code !== "IND") ? (
                        <Autocomplete
                          options={gstOptions}
                          disableClearable
                          getOptionLabel={(option: any) => `${option.gst_percentage}%` || ""}
                          isOptionEqualToValue={(option: any, value: any) =>
                            option.gst_id === value?.gst_id ||
                            Number(option.gst_percentage) === Number(value?.gst_percentage)
                          }
                          value={
                            item.gst_percent
                              ? gstOptions.find((opt: any) => Number(opt.gst_percentage) === Number(item.gst_percent)) || null
                              : null
                          }
                          onChange={(_, newValue: any) => {
                            if (newValue) {
                              handleItemUpdate(
                                "gst_percent",
                                Number(newValue.gst_percentage) || 0
                              );
                            } else {
                              handleItemUpdate("gst_percent", 0);
                            }
                          }}
                          ListboxProps={autocompleteDropdownStyles.ListboxProps}
                          renderOption={(props, option) =>
                            autocompleteDropdownStyles.renderOption(
                              props,
                              option,
                              (opt) => `${opt.gst_percentage}%`
                            )
                          }
                          renderInput={(params) => (
                            <BootstrapTextField
                              {...params}
                              placeholder="GST %"
                              size="small"
                              sx={{
                                ...autocompleteDropdownStyles.inputSx,
                         
      
                                 "& .MuiOutlinedInput-root": {
                                                                        height: "27px",
                                                                        width: "100%",
                                                                        fontSize: "13px",
                                                                        fontFamily: "Poppins, sans-serif",
                                                                        "& .MuiOutlinedInput-input": {
                                                                            padding: "6px 32px 6px 14px",
                                                                        },
                                                                        "& .MuiAutocomplete-endAdornment": {
                                                                            right: "8px",
                                                                            "& .MuiIconButton-root": {
                                                                                padding: "4px",
                                                                                "& svg": {
                                                                                    fontSize: "18px",
                                                                                },
                                                                            },
                                                                        },
                                                                    },
                              }}
                            />
                          )}
                        />
                      ) : (
                        `${item.gst_percent || 0}%`
                      )}
                    </td>

                    {/* Composition GST (If enabled) */}
                    {/* {composition_gst && (
                      <td style={{ width: "200px" }}>
                        <Form.Control
                          value={item.select_composition_gst}
                          disabled
                          size="sm"
                          onChange={(e) => {
                            // if (editableRowId === item.id) {
                            handleItemUpdate("composition_gst", e.target.value);
                            // }
                          }}
                        />
                      </td>
                    )} */}

                    {/* SGST / CGST / IGST */}
                    <td style={{ width: "100px", textAlign: "center" }}>
                      {item.sgst_percent || "-"}
                    </td>
                    <td style={{ width: "100px", textAlign: "center" }}>
                      {item.cgst_percent || "-"}
                    </td>
                    <td style={{ width: "100px", textAlign: "center" }}>
                      {item.igst_percent || "-"}
                    </td>

                    {/* GST Value */}
                    <td style={{ width: "100px", textAlign: "center" }} >
                      {(() => {
                        const currencySymbol = currency_symbol || "₹";
                        // If currency is not INR/IND, always show 0
                        const isForeignCurrency =
                          (item.currency_code && item.currency_code !== "INR" && item.currency_code !== "IND") ||
                          (currency_code && currency_code !== "INR" && currency_code !== "IND");

                        if (isForeignCurrency) {
                          return `${currencySymbol}0.00`;
                        }

                        // For INR/IND currency, show the actual GST value
                        const gstValue = item.gst_value !== null && item.gst_value !== undefined && item.gst_value !== ""
                          ? Number(item.gst_value || item.original_gst_value || 0)
                          : (item.original_gst_value !== null && item.original_gst_value !== undefined && item.original_gst_value !== ""
                            ? Number(item.original_gst_value)
                            : 0);
                        return `${currencySymbol}${gstValue.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`;
                      })()}
                    </td>

                    {/* Final Total */}
                    <td style={{ width: "100px", textAlign: "center" }}>
                      {(() => {
                        const currencySymbol = currency_symbol || "₹";
                        return `${currencySymbol}${parseFloat((item.total_amount).toString()).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`;
                      })()}
                    </td>

                    {/* Shipping Address */}
                    <td style={{
                      width: "300px",
                      textAlign: "center",
                      backgroundColor: (!item.shipping_address_id) ? "#fff3cd" : "transparent",
                      borderLeft: (!item.shipping_address_id) ? "3px solid #ffc107" : "none"
                    }}>
                      {editableRowId === item.id ? (
                        <Autocomplete
                          options={shippingAddresses || []}
                          getOptionLabel={(option) => option?.address || ""}
                          value={
                            shippingAddresses?.find(
                              (addr) => addr.id === item.shipping_address_id
                            ) || null
                          }
                          onChange={(_, val) => {
                            setItems((prevItems) => {
                              const updatedItems = [...prevItems];
                              const updatedItem = {
                                ...updatedItems[index],
                                shipping_address_id: val?.id || "",
                              };

                              // Recalculate totals and GST based on updated shipping
                              const recalculatedItem =
                                handleItemUpdateForItem(updatedItem);

                              updatedItems[index] = recalculatedItem;
                              return updatedItems;
                            });
                          }}
                          ListboxProps={autocompleteDropdownStyles.ListboxProps}
                          renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.id} style={{ padding: 0 }}>
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.8,
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: MecordColors.Hover,
                                    color: MecordColors.ActiveText,
                                  },
                                  ...(selected && {
                                    backgroundColor: MecordColors.ActiveBackground,
                                    color: MecordColors.ActiveText,
                                  }),
                                }}
                              >
                                <div style={{ fontWeight: "bold", fontSize: "0.85rem" }}>
                                  {option.address}
                                </div>
                                <div style={{ fontSize: "0.75rem", color: "#666" }}>
                                  {option.city}, {option.state} - {option.pin_code}
                                </div>
                              </Box>
                            </li>
                          )}
                          renderInput={(params) => (
                            <BootstrapTextField
                              {...params}
                              placeholder="Select Shipping Address"
                              size="small"
                              sx={autocompleteDropdownStyles.inputSx}
                            />
                          )}
                        />
                      ) : (
                        (() => {
                          const selectedAddress = shippingAddresses?.find(
                            (addr) => addr.id === item.shipping_address_id
                          );
                          return selectedAddress
                            ? `${selectedAddress.address}, ${selectedAddress.city}, ${selectedAddress.state} - ${selectedAddress.pin_code}`
                            : "-";
                        })()
                      )}
                    </td>

                    {/* Items availability */}

                    {/* {type == "product" && ( */}
                    <td
                      style={{
                        position: "sticky",
                        right: 100,
                        backgroundColor: MecordColors.LightGrayColor,
                        borderLeft: `1px solid ${MecordColors.LightGrayColor}`,
                        zIndex: 10,
                        width: "140px",
                        textAlign: "center",
                      }}
                    >
                      {editableRowId === item.id ? (
                        <Autocomplete
                          disableClearable
                          options={["In Stock", "Out of Stock"]}
                          getOptionLabel={(option) => String(option) || ""}
                          isOptionEqualToValue={(option, value) => String(option) === String(value)}
                          value={
                            item.availability && ["In Stock", "Out of Stock"].includes(item.availability)
                              ? item.availability
                              : "In Stock"
                          }
                          onChange={(_, newValue) => {
                            if (newValue) {
                              handleItemUpdate("availability", String(newValue));
                            }
                          }}
                          ListboxProps={autocompleteDropdownStyles.ListboxProps}
                          renderOption={(props, option) =>
                            autocompleteDropdownStyles.renderOption(
                              props,
                              option,
                              (opt) => opt || ""
                            )
                          }
                          renderInput={(params) => (
                            <BootstrapTextField
                              {...params}
                              size="small"
                              sx={{
                                ...autocompleteDropdownStyles.inputSx,
                                width: "140px",
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor:
                                    item.availability === "Out of Stock"
                                      ? "red"
                                      : item.availability === "In Stock"
                                        ? "skyblue"
                                        : "white",
                                  color: item.availability ? "white" : "black",
                                  fontSize: "13px",
                                  height: "32px",
                                  "& fieldset": {
                                    borderColor: "transparent",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "transparent",
                                  },
                                  "&.Mui-focused": {
                                    backgroundColor: "white",
                                    color: "black",
                                    "& fieldset": {
                                      borderColor: "transparent",
                                    },
                                  },
                                },
                                "& .MuiInputBase-input": {
                                  color: item.availability ? "white" : "black",
                                  fontSize: "13px",
                                  padding: "6px 14px",
                                  "&.Mui-focused": {
                                    color: "black",
                                  },
                                },
                                "& input": {
                                  color: item.availability ? "white" : "black",
                                },
                              }}
                            />
                          )}
                        />
                      ) : (
                        <span
                          style={{
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "13px",
                            backgroundColor:
                              item.availability === "Out of Stock"
                                ? "red"
                                : item.availability === "In Stock"
                                  ? "skyblue"
                                  : "transparent",
                            color: "white",
                            display: "inline-block",
                          }}
                        >
                          {item.availability || "In Stock"}
                        </span>
                      )}
                    </td>
                    {/* )} */}

                    {/* Actions */}
                    <td
                      style={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: MecordColors.LightGrayColor,
                        borderLeft: `1px solid ${MecordColors.LightGrayColor}`,
                        zIndex: 10,
                        width: "100px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      {editableRowId === item.id ? (
                        <SaveIcon
                          className="me-2"
                          style={{
                            cursor: "pointer",
                            color: "#28a745",
                            fontSize: "1.2rem"
                          }}
                          titleAccess="Save"
                          onClick={() => {
                            setEditableRowId(null);
                            toast.success("Row updated!");
                          }}
                        />
                      ) : (
                        <ModeEdit
                          className="me-2"
                          style={{
                            cursor: "pointer",
                            color: "#5dade2",
                            fontSize: "1.2rem"
                          }}
                          titleAccess="Edit Item"
                          onClick={() => setEditableRowId(item.id)}
                        />
                      )}

                      <Delete
                        style={{
                          cursor: "pointer",
                          color: "#dc3545",
                          fontSize: "1.2rem"
                        }}
                        titleAccess="Delete Item"
                        onClick={() => handleDelete(index)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="text-center text-muted py-4 border rounded p-3 bg-white">
          <img
            src={NoSelectedProducts}
            alt="No products selected"
            className="mb-3"
            style={{ maxWidth: "180px", height: "auto" }}
          />
          <div>Search existing products to add to this list or add</div>
          <div>new products to get started</div>
          {setShowProductModal && (
            <Button
              variant="light"
              className="mt-2"
              style={{
                backgroundColor: MecordColors.LightThemeBGColor,
                borderColor: MecordColors.LightThemeBGColor,
                minWidth: "80px",
                color: MecordColors.ThemeBGColor,
              }}
              onClick={() => {
                setShowProductModal(true);
              }}
            >
              {" "}
              + Add New Product
            </Button>
          )}
        </div>
      )}

      {/* Product Description Modal */}
      <Modal
        show={showDescriptionModal}
        onHide={() => {
          setShowDescriptionModal(false);
          setDescriptionModalContent("");
          setDescriptionModalProductName("");
        }}
        centered
      >
        <Modal.Header
          closeButton
          style={{
            fontFamily: tableStyles?.header?.fontFamily,
            fontSize: tableStyles?.header?.fontSize,
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <Modal.Title style={{ fontFamily: tableStyles?.header?.fontFamily, fontSize: tableStyles?.header?.fontSize }}>
            Product Description
            {descriptionModalProductName ? ` - ${descriptionModalProductName}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontFamily: filterPopoverStyles?.fontFamily, fontSize: filterPopoverStyles?.fontSize }}>
          {descriptionModalContent ? (
            <div
              className={DETAILS_BG}
              style={{ minHeight: "80px", fontFamily: filterPopoverStyles?.fontFamily, fontSize: filterPopoverStyles?.fontSize }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(descriptionModalContent) }}
            />
          ) : (
            <div className="text-muted" style={{ fontFamily: filterPopoverStyles?.fontFamily, fontSize: filterPopoverStyles?.fontSize }}>
              No description available.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button
            variant="secondary"
            className="rounded"
            style={{
              fontFamily: filterPopoverStyles?.button?.fontFamily,
              fontSize: filterPopoverStyles?.button?.fontSize,
              borderColor: filterPopoverStyles?.cancelButton?.borderColor,
              color: filterPopoverStyles?.cancelButton?.color,
              backgroundColor: "#fff",
            }}
            onClick={() => {
              setShowDescriptionModal(false);
              setDescriptionModalContent("");
              setDescriptionModalProductName("");
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Additional Details Modal */}
      <Modal
        show={showAdditionalDetailsModal}
        onHide={() => {
          setShowAdditionalDetailsModal(false);
          setAdditionalDetailsIndex(null);
          setAdditionalDetailsDraft("");
        }}
        centered
        className="modal-lg"
      >
        <Modal.Header
          closeButton
          style={{
            fontFamily: tableStyles?.header?.fontFamily,
            fontSize: tableStyles?.header?.fontSize,
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <Modal.Title style={{ fontFamily: tableStyles?.header?.fontFamily, fontSize: tableStyles?.header?.fontSize }}>
            Additional Details
            {additionalDetailsIndex !== null && items[additionalDetailsIndex]
              ? ` - ${items[additionalDetailsIndex]?.productName || items[additionalDetailsIndex]?.item_name}`
              : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontFamily: filterPopoverStyles?.fontFamily, fontSize: filterPopoverStyles?.fontSize }}>
          <Form.Group className="mb-0">
            <TextEditor
              value={additionalDetailsDraft}
              onChange={setAdditionalDetailsDraft}
              placeholder="Enter additional details..."
              minHeightPx={120}
              maxHeightPx={200}
              blockImages={true}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button
            variant="secondary"
            className="px-3 rounded"
            style={{
              fontFamily: filterPopoverStyles?.button?.fontFamily,
              fontSize: filterPopoverStyles?.button?.fontSize,
              borderColor: filterPopoverStyles?.cancelButton?.borderColor,
              color: filterPopoverStyles?.cancelButton?.color,
              backgroundColor: "#fff",
            }}
            onClick={() => {
              setShowAdditionalDetailsModal(false);
              setAdditionalDetailsIndex(null);
              setAdditionalDetailsDraft("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className="px-3 rounded"
            style={{
              fontFamily: filterPopoverStyles?.button?.fontFamily,
              fontSize: filterPopoverStyles?.button?.fontSize,
              backgroundColor: filterPopoverStyles?.applyButton?.backgroundColor,
              borderColor: filterPopoverStyles?.applyButton?.backgroundColor,
              color: "#fff",
            }}
            onClick={() => {
              if (additionalDetailsIndex !== null) {
                setItems((prev) => {
                  const next = [...prev];
                  const item = next[additionalDetailsIndex];
                  if (item) {
                    next[additionalDetailsIndex] = { ...item, additional_details: additionalDetailsDraft };
                  }
                  return next;
                });
              }
              setShowAdditionalDetailsModal(false);
              setAdditionalDetailsIndex(null);
              setAdditionalDetailsDraft("");
            }}
          >
            Update
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

ProductAndServiceSection.displayName = 'ProductAndServiceSection';

export default ProductAndServiceSection;
